import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { API } from '../api/client';
import type { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { cop, num, pct } from '../lib/format';
import type { ParametrosGlobales } from '../api/types';
import { PageHeader } from '../components/PageHeader';

type FormState = {
  margenObjetivoPct: string; // editado como porcentaje entero (ej. "30")
  paquetesMesEstimados: string;
  arriendoMensual: string;
  serviciosMensual: string;
  internetMensual: string;
  otrosFijosMensual: string;
  notaCostosFijos: string;
};

const empty: FormState = {
  margenObjetivoPct: '30',
  paquetesMesEstimados: '100',
  arriendoMensual: '0',
  serviciosMensual: '0',
  internetMensual: '0',
  otrosFijosMensual: '0',
  notaCostosFijos: '',
};

export function ConfiguracionPage() {
  const { tiene } = useAuth();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(empty);
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['parametros-globales'],
    queryFn: () => API.get<ParametrosGlobales>('/parametros'),
    enabled: tiene('parametros:read'),
  });

  useEffect(() => {
    if (q.data) {
      setForm({
        margenObjetivoPct: String(Math.round(Number(q.data.margenObjetivo) * 10000) / 100),
        paquetesMesEstimados: String(q.data.paquetesMesEstimados),
        arriendoMensual: String(q.data.arriendoMensual),
        serviciosMensual: String(q.data.serviciosMensual),
        internetMensual: String(q.data.internetMensual),
        otrosFijosMensual: String(q.data.otrosFijosMensual),
        notaCostosFijos: q.data.notaCostosFijos ?? '',
      });
    }
  }, [q.data]);

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) => API.put<ParametrosGlobales>('/parametros', body),
    onSuccess: () => {
      setMsg('Cambios guardados.');
      setErr(null);
      qc.invalidateQueries({ queryKey: ['parametros-globales'] });
      setTimeout(() => setMsg(null), 2500);
    },
    onError: (e: unknown) => {
      setErr((e as ApiError).message ?? 'Error al guardar');
      setMsg(null);
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    const margen = Number(form.margenObjetivoPct) / 100;
    if (!(margen >= 0 && margen < 0.95)) {
      setErr('El margen debe ser un porcentaje entre 0 y 95.');
      return;
    }
    save.mutate({
      margenObjetivo: margen,
      paquetesMesEstimados: Number(form.paquetesMesEstimados),
      arriendoMensual: Number(form.arriendoMensual),
      serviciosMensual: Number(form.serviciosMensual),
      internetMensual: Number(form.internetMensual),
      otrosFijosMensual: Number(form.otrosFijosMensual),
      notaCostosFijos: form.notaCostosFijos.trim() || null,
    });
  }

  const puedeEditar = tiene('parametros:write');
  const totalFijos =
    Number(form.arriendoMensual || 0) +
    Number(form.serviciosMensual || 0) +
    Number(form.internetMensual || 0) +
    Number(form.otrosFijosMensual || 0);
  const paqMes = Number(form.paquetesMesEstimados || 0);
  const aporteAFijoPaq = paqMes > 0 ? totalFijos / paqMes : 0;

  if (!tiene('parametros:read')) {
    return (
      <div className="card p-4 text-sm text-slate-600">
        No tienes permiso para ver parámetros. Pide a un administrador el permiso{' '}
        <code>parametros:read</code>.
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <PageHeader
        title="Configuración"
        subtitle="Margen objetivo, paquetes estimados al mes y costos fijos recurrentes (arriendo, servicios…)."
      />

      <form onSubmit={onSubmit} className="space-y-4 sm:space-y-6">
        {/* Margen y producción estimada */}
        <div className="card p-4 sm:p-5 space-y-4">
          <div>
            <h2 className="font-semibold">Margen objetivo y producción estimada</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              El margen objetivo se usa para calcular el <b>precio sugerido</b> de cada paquete.
              Los <b>paquetes estimados al mes</b> se usan para prorratear los costos fijos
              (depreciación + arriendo + servicios…) en el costo fijo por paquete.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">Margen objetivo (%)</label>
              <input
                type="number"
                inputMode="decimal"
                step="0.1"
                min={0}
                max={95}
                className="input"
                value={form.margenObjetivoPct}
                onChange={(e) => setForm({ ...form, margenObjetivoPct: e.target.value })}
                disabled={!puedeEditar}
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Equivale a {pct(Number(form.margenObjetivoPct || 0) / 100)}.
              </p>
            </div>
            <div>
              <label className="label">Paquetes estimados al mes</label>
              <input
                type="number"
                inputMode="numeric"
                min={1}
                className="input"
                value={form.paquetesMesEstimados}
                onChange={(e) => setForm({ ...form, paquetesMesEstimados: e.target.value })}
                disabled={!puedeEditar}
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Cuántos paquetes proyectas producir/vender en un mes típico.
              </p>
            </div>
          </div>
        </div>

        {/* Costos fijos mensuales */}
        <div className="card p-4 sm:p-5 space-y-4">
          <div>
            <h2 className="font-semibold">Costos fijos mensuales</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Gastos recurrentes que no dependen del volumen de producción. Se suman al costo
              fijo de cada paquete junto con la depreciación de tus equipos.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <MontoCOP
              label="Arriendo"
              value={form.arriendoMensual}
              onChange={(v) => setForm({ ...form, arriendoMensual: v })}
              disabled={!puedeEditar}
            />
            <MontoCOP
              label="Servicios públicos (luz/agua/alcantarillado fijos)"
              value={form.serviciosMensual}
              onChange={(v) => setForm({ ...form, serviciosMensual: v })}
              disabled={!puedeEditar}
            />
            <MontoCOP
              label="Internet / teléfono"
              value={form.internetMensual}
              onChange={(v) => setForm({ ...form, internetMensual: v })}
              disabled={!puedeEditar}
            />
            <MontoCOP
              label="Otros fijos (seguros, licencias…)"
              value={form.otrosFijosMensual}
              onChange={(v) => setForm({ ...form, otrosFijosMensual: v })}
              disabled={!puedeEditar}
            />
          </div>

          <div>
            <label className="label">Nota (opcional)</label>
            <textarea
              className="input min-h-[60px]"
              placeholder="Ej: arriendo incluye agua, internet es plan de 30 megas…"
              value={form.notaCostosFijos}
              onChange={(e) => setForm({ ...form, notaCostosFijos: e.target.value })}
              disabled={!puedeEditar}
            />
          </div>

          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Resumen label="Total costos fijos / mes" value={cop(totalFijos)} strong />
            <Resumen label="Paquetes estimados / mes" value={num(paqMes, 0)} />
            <Resumen
              label="Aporte al costo fijo / paq"
              hint="Sin contar depreciación de equipos"
              value={cop(aporteAFijoPaq)}
              strong
            />
          </div>
        </div>

        {err && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-3">{err}</div>
        )}
        {msg && (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3">{msg}</div>
        )}

        <div className="flex flex-col sm:flex-row sm:justify-end gap-2">
          <button
            type="submit"
            className="btn-accent"
            disabled={!puedeEditar || save.isPending || q.isLoading}
          >
            {save.isPending ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>

        {!puedeEditar && (
          <p className="text-xs text-slate-500">
            Solo lectura. Necesitas el permiso <code>parametros:write</code> para editar.
          </p>
        )}
      </form>
    </div>
  );
}

function MontoCOP({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="label">{label}</label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">$</span>
        <input
          type="number"
          inputMode="decimal"
          min={0}
          step="1000"
          className="input pl-7"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
      </div>
    </div>
  );
}

function Resumen({
  label,
  value,
  hint,
  strong,
}: {
  label: string;
  value: string;
  hint?: string;
  strong?: boolean;
}) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`${strong ? 'text-lg font-semibold' : 'text-sm'} text-slate-800 mt-0.5 break-words`}>
        {value}
      </div>
      {hint && <div className="text-[11px] text-slate-500 mt-0.5">{hint}</div>}
    </div>
  );
}
